import * as Yup from 'yup';
import Flatpickr from 'react-flatpickr';
import PropTypes from 'prop-types';
import React from 'react';
import { FormFeedback, Input } from 'reactstrap';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, useHistory, Link } from 'react-router-dom';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import FormCreatePromotion from 'components/FormCreatePromotion';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';
import SelectValidation from 'components/App/SelectValidation';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_STATUS, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { createLastMinutePromotionValidation, createPromotionValidation } from 'utils/validate';

import { ReactComponent as LastMinutePromotionIcon } from 'assets/images/last-minute-promotion.svg';

import styles from './CreateLastMinutePromotion.module.scss';

// BREADCRUMB constant moved inside component dynamically

const optionFlatpickr = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  minuteIncrement: 1,
  time_24hr: true,
};

const optionSelect = [
  {
    label: '1',
    value: 1,
  },
  {
    label: '2',
    value: 2,
  },
  {
    label: '3',
    value: 3,
  },
];

function CreateLastMinutePromotion(props) {
  const {
    isLoadingCreatePromotion,

    roomOfPlaceList,
    placePriceTypes,
    promotionDetail,

    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionGetPromotionDetail,
    actionPostPromotion,
    actionEditPromotion,
    actionClearDataPromotionDetail,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: '',
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: false,
      link: placeId
        ? `${LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION}?place=${placeId}`
        : LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
    },
    {
      name: 'Ưu Đãi Phút Chót',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      rooms: [],
      isAllRooms: true,
      isAllPriceTypes: true,
      applyBookingDays: DATES_OF_WEEK,
      priceTypes: [],
      bookingStartAt: '',
      bookingEndAt: '',
      stayAt: [{
        _id: '1',
        startAt: '',
        endAt: '',
      }],
      discountRate: '',
      discountType: PROMOTION_TYPE.LAST_DAY,
      name: '',
      maxLastDays: 3,
      applyTimes: {
        from: '00:00',
        to: '00:00',
      },
      isRequired: false,
    },

    validationSchema: Yup.object({
      rooms: createPromotionValidation.rooms,
      priceTypes: createPromotionValidation.priceTypes,
      applyBookingDays: createPromotionValidation.applyBookingDays,
      discountRate: createPromotionValidation.discountRate,
      bookingStartAt: createPromotionValidation.bookingStartAt,
      bookingEndAt: createPromotionValidation.bookingEndAt,
      stayAt: createPromotionValidation.stayAt,
      name: createPromotionValidation.name,
      applyTimes: createLastMinutePromotionValidation.applyTimes,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const onChangeRequired = React.useCallback((e) => {
    validation.setFieldValue('isRequired', !!+e.target.value);
  }, []);

  const handleTimeChange = React.useCallback((_, currentDate, self) => {
    validation.setValues((values) => ({
      ...values,
      applyTimes: {
        ...values.applyTimes,
        [self.element.name]: currentDate,
      },
    }));
  }, []);

  const onChangeDate = React.useCallback((e) => {
    validation.setFieldValue('maxLastDays', e.value);
  }, []);

  const onCallback = React.useCallback(() => {
    onCloseModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=PENDING`,
    });
  }, [placeId, promotionId]);

  const onSubmitCreatePromotion = React.useCallback(() => {
    const newPromotion = {
      ...validation.values,
      applyBookingDays: validation.values.applyBookingDays
        .filter((item) => item.isChecked)
        .map((item) => item.value),
      stayAt: validation.values.stayAt.map((item) => ({
        startAt: item.startAt,
        endAt: item.endAt,
      })),
    };

    if (!newPromotion.isRequired) delete newPromotion.applyTimes;

    if (promotionId) {
      actionEditPromotion({
        placeId,
        promotionId,
        promotion: newPromotion,
        callback: onCallback,
      });
    } else {
      actionPostPromotion({
        placeId,
        promotion: newPromotion,
        callback: onCallback,
      });
    }
  }, [validation.values, placeId, promotionId]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetPlacePriceTypes({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPromotionDetail();
    };
  }, [promotionId, placeId]);

  React.useEffect(() => {
    if (Object.keys(promotionDetail).length > 0) {
      validation.setValues((values) => ({
        ...values,
        ...promotionDetail,
        discountRate: promotionDetail.discount,
        applyBookingDays: promotionDetail.applyBookingDays ? DATES_OF_WEEK.map((item) => ({
          ...item,
          isChecked: promotionDetail.applyBookingDays.includes(item.value),
        })) : DATES_OF_WEEK,
        isRequired: !!promotionDetail.applyTimes,
      }));
    }

    return () => {
      validation.resetForm();
    };
  }, [promotionDetail]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="Tạo ưu đãi phút chót"
        breadcrumbList={breadcrumbList}
      />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <LastMinutePromotionIcon className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Phút Chót</h3>
              <p>
                Lấp đầy những phòng trống còn lại của Quý vị.
                Khuyến mãi này dành cho khách đặt một vài ngày trước ngày nhận phòng.
              </p>
            </div>
          </div>
        </Card>

        <Card className={styles.Section}>
          <h3 className={styles.Title}>Thời gian đặt</h3>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>
              Khách cần đặt với khuyến mãi này trước bao lâu?
            </p>

            <div className="d-flex gap-2 align-items-center">
              <SelectValidation
                placeholder="Chọn ngày"
                options={optionSelect}
                value={validation.values.maxLastDays}
                validation={validation}
                onChange={onChangeDate}
                selectedClassName={styles.Select}
              />

              <span>ngày hoặc ít hơn trước khi nhận phòng</span>
            </div>
          </div>

          <div className={styles.FormField}>
            <p className={styles.FormFieldTitle}>
              Quý vị có muốn giới hạn việc áp dụng khuyến mãi này vào một số giờ nhất định?
            </p>

            <label htmlFor="notRequire">
              <Input
                id="notRequire"
                name="isRequired"
                value={0}
                type="radio"
                validation={validation}
                onChange={onChangeRequired}
                checked={validation.values.isRequired === false}
              />

              <span>Không</span>
            </label>

            <div>
              <label htmlFor="require">
                <Input
                  id="require"
                  name="isRequired"
                  value={1}
                  type="radio"
                  validation={validation}
                  onChange={onChangeRequired}
                  checked={validation.values.isRequired === true}
                />

                <span>Có</span>
              </label>

              {validation.values.isRequired && (
              <div className={styles.SelectTime}>
                <Flatpickr
                  name="from"
                  className={styles.TimePicker}
                  defaultValue={validation.values.applyTimes.from}
                  options={optionFlatpickr}
                  onChange={handleTimeChange}
                  value={validation.values.applyTimes.from}
                />
                -
                <Flatpickr
                  name="to"
                  className={styles.TimePicker}
                  defaultValue={validation.values.applyTimes.to}
                  options={optionFlatpickr}
                  onChange={handleTimeChange}
                  value={validation.values.applyTimes.to}
                />
              </div>
              )}
              {validation.errors?.applyTimes && validation.touched?.applyTimes && (
              <FormFeedback type="invalid" className="d-block font-size-12">
                {validation.errors.applyTimes}
              </FormFeedback>
              )}
            </div>
          </div>
        </Card>

        <FormCreatePromotion
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
        />

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link to={{
              pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
              search: `?place=${placeId}`,
            }}
            >
              <Button
                color={themeColor.SECONDARY}
              >
                Hủy
              </Button>
            </Link>
            <Button
              onClick={validation.handleSubmit}
              disabled={promotionDetail.discountStatus
                && promotionDetail.discountStatus !== PROMOTION_STATUS.PENDING}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePromotion}
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreatePromotion}
        />
      </div>
    </div>
  );
}

CreateLastMinutePromotion.propTypes = {
  isLoadingCreatePromotion: PropTypes.bool.isRequired,

  roomOfPlaceList: PropTypes.instanceOf(Array),
  placePriceTypes: PropTypes.instanceOf(Array),
  promotionDetail: PropTypes.instanceOf(Object),

  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionGetPlacePriceTypes: PropTypes.func.isRequired,
  actionPostPromotion: PropTypes.func.isRequired,
  actionGetPromotionDetail: PropTypes.func.isRequired,
  actionEditPromotion: PropTypes.func.isRequired,
  actionClearDataPromotionDetail: PropTypes.func.isRequired,
};

CreateLastMinutePromotion.defaultProps = {
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  placePriceTypes: INITIAL_VALUE.ARRAY,
  promotionDetail: INITIAL_VALUE.OBJECT,
};

export default CreateLastMinutePromotion;
